import React from 'react';
import { Eye, X, MapPin, Maximize, Bed, Bath, Calendar, Building2, Share2 } from 'lucide-react';
import { formatPrice, formatArea, getPropertyTypeColor, getStatusColor } from '../utils/helpers';

const getOfferImage = (offer) => {
  if (!offer) return null;
  if (offer.coverImage) return offer.coverImage;
  if (Array.isArray(offer.images) && offer.images.length > 0) return offer.images[0];
  return null;
};

const getTypeLabel = (offer, propertyTypes) => {
  const typeValue = offer?.propertyType || offer?.type;
  if (!typeValue) return 'عقار';
  return propertyTypes.find((t) => t.value === typeValue)?.label || typeValue;
};

const getStatusLabel = (status) => {
  if (status === 'available') return 'متاح';
  if (status === 'reserved') return 'محجوز';
  if (status === 'sold') return 'مباع';
  if (status === 'rented') return 'مؤجر';
  return status || '-';
};

const getOfferNatureLabel = (value) => {
  if (value === 'owner') return 'مع المالك';
  if (value === 'direct') return 'مباشر';
  if (value === 'withDirect') return 'مع المباشر';
  if (value === 'multiple') return 'أكثر من وسيط';
  if (value === 'indirect') return 'غير مباشر';
  return value || '-';
};

const OfferDetailsModal = ({
  isOpen,
  offer,
  onClose,
  onShare,
  propertyTypes = []
}) => {
  if (!isOpen || !offer) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <Eye size={24} />
            تفاصيل العرض
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="view-offer-content">
            <div className="view-offer-image">
              {getOfferImage(offer) ? (
                <img src={getOfferImage(offer)} alt={offer.title || 'offer'} />
              ) : (
                <div className="no-image-placeholder" style={{ width: '100%', height: '300px', borderRadius: '12px', fontSize: '16px' }}>
                  لا توجد صور
                </div>
              )}
              <div className="view-badges">
                <span className="type-badge" style={getPropertyTypeColor(offer.propertyType || offer.type)}>
                  {getTypeLabel(offer, propertyTypes)}
                </span>
                {offer.offerType && (
                  <span className="offer-type-badge" style={{ background: offer.offerType === 'sale' ? '#3b82f6' : '#8b5cf6', color: 'white' }}>
                    {offer.offerType === 'sale' ? 'بيع' : 'إيجار'}
                  </span>
                )}
                {offer.status && (
                  <span className="status-badge" style={getStatusColor(offer.status)}>
                    {getStatusLabel(offer.status)}
                  </span>
                )}
              </div>
            </div>

            <div className="view-offer-details">
              <h3>{offer.title || 'بدون عنوان'}</h3>

              {(offer.city || offer.district) && (
                <div className="view-location">
                  <MapPin size={18} />
                  <span>{offer.city || '-'}{offer.district ? ` - ${offer.district}` : ''}</span>
                </div>
              )}

              {offer.price ? <div className="view-price">{formatPrice(offer.price)}</div> : null}

              <div className="view-specs-grid">
                {offer.area > 0 && (
                  <div className="spec-card">
                    <Maximize size={20} />
                    <div className="spec-info">
                      <span className="spec-label">المساحة</span>
                      <span className="spec-value">{formatArea(offer.area)}</span>
                    </div>
                  </div>
                )}
                {offer.rooms > 0 && (
                  <div className="spec-card">
                    <Bed size={20} />
                    <div className="spec-info">
                      <span className="spec-label">الغرف</span>
                      <span className="spec-value">{offer.rooms}</span>
                    </div>
                  </div>
                )}
                {offer.bathrooms > 0 && (
                  <div className="spec-card">
                    <Bath size={20} />
                    <div className="spec-info">
                      <span className="spec-label">الحمامات</span>
                      <span className="spec-value">{offer.bathrooms}</span>
                    </div>
                  </div>
                )}
                {offer.age > 0 && (
                  <div className="spec-card">
                    <Calendar size={20} />
                    <div className="spec-info">
                      <span className="spec-label">عمر العقار</span>
                      <span className="spec-value">{offer.age} سنة</span>
                    </div>
                  </div>
                )}
                {offer.floors > 0 && (
                  <div className="spec-card">
                    <Building2 size={20} />
                    <div className="spec-info">
                      <span className="spec-label">الأدوار</span>
                      <span className="spec-value">{offer.floors}</span>
                    </div>
                  </div>
                )}
              </div>

              {(offer.deedType || offer.offerNature || offer.direction || offer.streetWidth) && (
                <div className="view-additional-details">
                  <h4>تفاصيل إضافية</h4>
                  <div className="details-grid">
                    {offer.deedType && (
                      <div className="detail-item">
                        <span className="detail-label">نوع الصك:</span>
                        <span className="detail-value">{offer.deedType === 'free' ? 'حر' : 'مرهون'}</span>
                      </div>
                    )}
                    {offer.offerNature && (
                      <div className="detail-item">
                        <span className="detail-label">صفة العرض:</span>
                        <span className="detail-value">{getOfferNatureLabel(offer.offerNature)}</span>
                      </div>
                    )}
                    {offer.direction && (
                      <div className="detail-item">
                        <span className="detail-label">الواجهة:</span>
                        <span className="detail-value">{offer.direction}</span>
                      </div>
                    )}
                    {offer.streetWidth > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">عرض الشارع:</span>
                        <span className="detail-value">{offer.streetWidth} م</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {offer.description && (
                <div className="view-description">
                  <h4>الوصف</h4>
                  <p>{offer.description}</p>
                </div>
              )}

              {Array.isArray(offer.features) && offer.features.length > 0 && (
                <div className="view-features">
                  <h4>المميزات</h4>
                  <div className="features-list">
                    {offer.features.map((feature, idx) => (
                      <span key={idx} className="feature-tag">{feature}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>إغلاق</button>
          {onShare && (
            <button className="btn btn-secondary" onClick={() => onShare(offer)}>
              <Share2 size={18} />
              مشاركة
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferDetailsModal;
